import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NoteItem from './NoteItem';
import { useDispatch, useSelector } from 'react-redux';
import { deleteNote, fetchAllNotes, setCurrentlyEditingNote } from '@actions/notes';
import Note, { removeNotePrompt } from '@models/Note';
import { createListContext, List as VirtualizedList } from '@components/List/VirtualizedList';
import styles from './Notes.module.scss';
import { ViewID } from '@reducers/ui/viewRestoration';
import { openExistingNoteInModal, selectSavedViewState } from '@actions/ui';
import analytics from '@support/analytics';
import { useAppContactSelecting } from '@hooks/use-app-contact-selecting';
import { useSaveViewState } from '@hooks/use-save-view-state';
import { useScrollRestoration } from '@hooks/use-scroll-restoration';
import EmptyNotes from './EmptyNotes';
import Container from '@components/ContentContainer';
import Header from '@components/Header';
import { selectNotes } from '@reducers/data/notes';
import { ContactPreviewSidebar } from '@components/Contact/Sidebar';
import isModifiedEvent from '@support/isModifiedEvent';
import { NotesViewTitle } from './NotesViewTitle';

const NoteListContext = createListContext();
const viewID: ViewID = 'Notes';

export default function Notes() {
  const dispatch = useDispatch();
  const { items, isLoaded, canLoadMore } = useSelector(selectNotes);
  const scrollingContainerRef = useRef(null);
  const notesList = items.valueSeq().toArray();
  const noteIndexes = useMemo(
    () =>
      items
        .valueSeq()
        .toArray()
        .reduce((indices, { id }, index) => ({ ...indices, [id]: index }), {}),
    [items],
  );
  const savedViewState = useSelector(selectSavedViewState(viewID));
  const [highlightedNote, setHighlightedNote] = useState(savedViewState?.highlightedNote);
  const listRef = useRef(null);
  const onNoteHighlight = useCallback((note) => {
    setHighlightedNote(note);
  }, []);
  useSaveViewState(viewID, { highlightedNote }, [highlightedNote]);

  useEffect(() => {
    analytics.track('Notes Viewed');
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      dispatch(fetchAllNotes());
    }

    return () => {
      dispatch(setCurrentlyEditingNote(null));
    };
  }, []);

  useAppContactSelecting({ contact: highlightedNote?.contact, source: 'notes-index' });
  useScrollRestoration({ viewID: 'Notes', scrollingContainerRef });

  const defaultHighlightedIdx = useMemo(() => {
    let idx = 0;
    if (savedViewState?.highlightedNote) {
      idx = noteIndexes[savedViewState.highlightedNote?.id];
    }

    return idx || 0;
  }, []);

  const openNoteInModal = (note: Note, focusMode?: boolean) => {
    dispatch(openExistingNoteInModal({ contact: note.contact, note, focusMode }));
  };

  const promptToDeleteNote = (note, e) => removeNotePrompt(() => dispatch(deleteNote(note)))(e);

  const handleHotKey = (note, e) => {
    if (highlightedNote && e.key?.toLowerCase() === 'e' && !note.isReadOnly) {
      e.preventDefault();
      promptToDeleteNote(note, e);
    }

    if (highlightedNote && e.key?.toLowerCase() === 'f') {
      e.preventDefault();
      openNoteInModal(note, true);
    }
  };

  const renderItem = useCallback((note, isHighlighted) => {
    return (
      <NoteItem
        key={note.id}
        active={isHighlighted}
        onSelect={openNoteInModal}
        onDelete={promptToDeleteNote}
        note={note}
      />
    );
  }, []);

  return (
    <Container>
      <Container.Primary
        header={<Header title={<NotesViewTitle />} actions={<></>} />}
        className={styles.container}
      >
        {isLoaded && notesList.length === 0 && <EmptyNotes />}
        <VirtualizedList
          shouldResetHighlight={false}
          items={notesList}
          topPadding={48}
          onHotKey={handleHotKey}
          renderItem={renderItem}
          onItemClick={(note: Note, e) => {
            if (e.defaultPrevented) {
              return;
            }
            openNoteInModal(note, isModifiedEvent(e));
          }}
          canLoadMore={canLoadMore}
          loadMore={() => dispatch(fetchAllNotes())}
          ListContext={NoteListContext}
          onItemHighlight={onNoteHighlight}
          headerCutoff={1}
          listRef={listRef}
          listRowClassName={styles.listRow}
          ref={scrollingContainerRef}
          defaultHighlightedIdx={defaultHighlightedIdx}
          indicateRowLoadingState={false}
        />
      </Container.Primary>
      <ContactPreviewSidebar contactID={highlightedNote?.contact?.id} />
    </Container>
  );
}
